/**
 * MissingParametersDialog Component
 *
 * Dialog component to collect missing required parameters for questions before saving.
 * Currently handles audio-recording questions that need preparationTime and recordingDuration.
 *
 * @module MissingParametersDialog
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface MissingParametersDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly questions: any[];
  readonly onApply: (params: {
    preparationTime: number;
    recordingDuration: number;
  }) => void;
}

/**
 * MissingParametersDialog Component
 *
 * Shows a dialog to collect missing parameters for questions before saving.
 * Currently handles audio-recording questions that need recordingDuration.
 * Live-conversation questions do not require a recording duration and are excluded.
 *
 * @param open - Whether the dialog is open
 * @param onOpenChange - Callback when dialog open state changes
 * @param questions - Array of questions that need parameters
 * @param onApply - Callback when user applies the parameters (should then proceed with save)
 */
export default function MissingParametersDialog({
  open,
  onOpenChange,
  questions,
  onApply,
}: MissingParametersDialogProps) {
  const [preparationTime, setPreparationTime] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(60);
  const [errors, setErrors] = useState<{
    preparationTime?: string;
    recordingDuration?: string;
  }>({});

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setPreparationTime(0);
      setRecordingDuration(60);
      setErrors({});
    }
  }, [open]);

  const validate = (): boolean => {
    const newErrors: { preparationTime?: string; recordingDuration?: string } =
      {};

    // Validate preparation time (0-600)
    if (preparationTime < 0 || preparationTime > 600) {
      newErrors.preparationTime =
        'Preparation time must be between 0 and 600 seconds';
    }

    // Validate recording duration (1-600)
    if (recordingDuration < 1 || recordingDuration > 600) {
      newErrors.recordingDuration =
        'Recording duration must be between 1 and 600 seconds';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = () => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }

    onApply({
      preparationTime,
      recordingDuration,
    });

    onOpenChange(false);
  };

  const audioRecordingCount = questions.filter(
    (q) => q.type === 'audio-recording',
  ).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-ui">Missing Parameters</DialogTitle>
          <DialogDescription className="font-ui">
            Some questions are missing required parameters. Please provide the
            following information before saving.
            {audioRecordingCount > 0 && (
              <span className="block mt-1 text-sm">
                {audioRecordingCount} audio recording question
                {audioRecordingCount > 1 ? 's' : ''}{' '}
                {audioRecordingCount > 1 ? 'need' : 'needs'} parameters.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {audioRecordingCount > 0 && (
            <>
              <div>
                <Label htmlFor="preparationTime" className="font-ui">
                  Preparation Time (seconds)
                </Label>
                <Input
                  id="preparationTime"
                  type="number"
                  min={0}
                  max={600}
                  value={preparationTime}
                  onChange={(e) =>
                    setPreparationTime(Number.parseInt(e.target.value) || 0)
                  }
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  Time allowed for preparation before recording (0–600 seconds)
                </p>
                {errors.preparationTime && (
                  <p className="text-xs text-destructive mt-1 font-ui">
                    {errors.preparationTime}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="recordingDuration" className="font-ui">
                  Recording Duration (seconds)
                </Label>
                <Input
                  id="recordingDuration"
                  type="number"
                  min={1}
                  max={600}
                  value={recordingDuration}
                  onChange={(e) =>
                    setRecordingDuration(Number.parseInt(e.target.value) || 1)
                  }
                  className="mt-1"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1 font-ui">
                  Maximum duration for the audio recording (1-600 seconds)
                </p>
                {errors.recordingDuration && (
                  <p className="text-xs text-destructive mt-1 font-ui">
                    {errors.recordingDuration}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <span className="font-ui">Cancel</span>
          </Button>
          <Button onClick={handleApply}>
            <span className="font-ui">Apply & Save</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
